import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {api, getApiDomain, getApiUrl, getCompanyId, getStorageUrl, getWhatsAppUrl} from '../config/api';
import { BackEndRoutes } from '../config/back-end-routes';

export interface SiteConfig {
  site_title: string;
  site_description: string;
  logo_url: string;
  favicon_url: string;
  primary_color: string;
  secondary_color: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  latitude: string;
  longitude: string;
  facebook_url: string;
  instagram_url: string;
  linkedin_url: string;
  about_text: string;
  mission: string;
  vision: string;
  values: string;
  cnpj: string;
  founded_year: string;
  established_text: string;
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  services_header_title: string;
  services_header_subtitle: string;
  services_section_title: string;
  services_section_description: string;
  business_hours: { day: string; hours: string }[] | null;
  show_team: boolean;
  show_services: boolean;
  show_about: boolean;
  show_contact: boolean;
  site_active: boolean;
}

export interface TeamMember {
  id: number;
  user_name: string;
  user_email: string;
  profile_photo: string | null;
  instagram_handle: string | null;
  cro_number: string | null;
  cro_uf: string | null;
  bio: string | null;
  site_description: string | null;
  experience_years: number | null;
  tipo: string;
  photo: { id: number; external_id: string; company_user_id: number; image_path: string } | null;
}

export interface ServiceGroup {
  id: number;
  external_id: string;
  description: string;
  detailed_description: string | null;
  icon_class: string | null;
  image_url: string | null;
  display_mode: string | null;
  highlight: boolean;
  features: string | null;
  order: number;
  services: { id: number; group_id: number; description: string }[];
  photo: { id: number; external_id: string; group_service_id: number; image_path: string; order: number } | null;
}

export interface PageSectionButton {
  id: number;
  external_id: string;
  text: string;
  url: string;
  class_name: string | null;
  is_link: boolean;
  variant: string | null;
  size: string | null;
  icon: string | null;
  order: number;
}

export interface PageSectionItem {
  id: number;
  external_id: string;
  item_type: string | null;
  item_title: string | null;
  item_description: string | null;
  item_icon: string | null;
  item_image: string | null;
  item_url: string | null;
  item_data: any;
  order: number;
}

export interface PageSection {
  id: number;
  external_id: string;
  section_key: string | null;
  section_title: string | null;
  section_subtitle: string | null;
  section_content: string | null;
  section_image: string | null;
  section_icon: string | null;
  section_data: any;
  order: number;
  items: PageSectionItem[];
  buttons: PageSectionButton[];
}

export interface PagePhoto {
  id: number;
  external_id: string;
  image_path: string;
  order: number;
}

export interface SitePage {
  id: number;
  external_id: string;
  page_key: string;
  page_title: string;
  page_description: string | null;
  meta_title: string | null;
  meta_description: string | null;
  order: number;
  sections: PageSection[];
  photos: PagePhoto[];
}

export interface GalleryPhoto {
  id: number;
  image_path: string;
  order: number;
}

interface SiteContextType {
  config: SiteConfig | null;
  team: TeamMember[];
  serviceGroups: ServiceGroup[];
  pages: SitePage[];
  galleryPhotos: GalleryPhoto[];
  loading: boolean;
  error: string | null;
  storageUrl: string;
  whatsappUrl: string;
  logoUrl: (path: string | null | undefined) => string;
  imageUrl: (path: string | null | undefined) => string;
  getPage: (pageKey: string) => SitePage | undefined;
}

const SiteContext = createContext<SiteContextType>({
  config: null,
  team: [],
  serviceGroups: [],
  pages: [],
  galleryPhotos: [],
  loading: true,
  error: null,
  storageUrl: '',
  whatsappUrl: '',
  imageUrl: () => '',
  logoUrl: () => '',
  getPage: () => undefined,
});

export const useSite = () => useContext(SiteContext);

export const SiteProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<SiteConfig | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [serviceGroups, setServiceGroups] = useState<ServiceGroup[]>([]);
  const [pages, setPages] = useState<SitePage[]>([]);
  const [galleryPhotos, setGalleryPhotos] = useState<GalleryPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const storageUrl = getStorageUrl();
  const whatsappUrl = getWhatsAppUrl();

  const imageUrl = (path: string | null | undefined): string => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${storageUrl}/${path}`;
  };

  const apiBaseUrl = getApiUrl().replace('/api', '');
  const logoUrl = (path: string | null | undefined): string => {
    if (!path) return '';
    if (path.startsWith('http')) return path;

    let url = getApiDomain() + path;
    console.log(url)
    return url;
  };

  const getPage = (pageKey: string): SitePage | undefined =>
    pages.find(p => p.page_key === pageKey);

  useEffect(() => {
    const companyId = getCompanyId();
    if (!companyId) {
      setError('COMPANY_ID não configurado');
      setLoading(false);
      return;
    }

    const { PUBLIC } = BackEndRoutes;

    const fetchAll = async () => {
      try {
        const [cfgRes, teamRes, svcRes, pagesRes, galleryRes] = await Promise.all([
          api.get(PUBLIC.SITE_CONFIG),
          api.get(PUBLIC.TEAM),
          api.get(PUBLIC.SERVICE_GROUPS),
          api.get(PUBLIC.PAGES).catch(() => ({ data: [] })),
          api.get(PUBLIC.GALLERY).catch(() => ({ data: [] })),
        ]);
        setConfig(cfgRes.data || null);
        setTeam(teamRes.data || []);
        setServiceGroups(svcRes.data || []);
        setPages(pagesRes.data || []);
        setGalleryPhotos(galleryRes.data || []);
      } catch (err: any) {
        console.error('Erro ao carregar dados do site:', err);
        setError('Erro ao carregar dados do site');
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  return (
    <SiteContext.Provider value={{ config, team, serviceGroups, pages, galleryPhotos, loading, error, storageUrl, whatsappUrl, imageUrl, logoUrl, getPage }}>
      {children}
    </SiteContext.Provider>
  );
};
